import { TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '../../../../../gen-src/Localize';
import {
  MetadataTable,
  SiteUrlsContext,
  TableauUser,
  UserLinkDisplayComponent,
  ViewerPaneObfuscatedPlaceholder,
} from '../../../data-catalog-shared';
import { LabelWrapper } from '../Labels/LabelsHookHelper';
import { HeaderWrapper } from './ViewerPaneStyles';
import { VizClientDatasource } from './VizClientInterfaces';

export const headerDetailsHeaderTestId = 'data-details-about-section-header';

export interface HeaderDetailsProps {
  readonly viewId: string;
  readonly viewName: string | null;
  readonly workbookName: string | null;
  readonly workbookVizportalUrlId: string;
  readonly projectName: string | null;
  readonly projectVizportalUrlId: string | null;
  readonly owner: TableauUser | null;
  readonly modifiedDate?: string;
  readonly sparklineElement: JSX.Element | null;
  readonly datasources: ReadonlyArray<VizClientDatasource>;
}

/** A widget to display author and timestamp information in the lineage panel */
export function HeaderDetails(props: HeaderDetailsProps) {
  const view = {
    id: props.viewId,
    name: props.viewName,
    __typename: 'View'
  };

  return (
    <HeaderWrapper>
      {props.sparklineElement}
      <MetadataTable.Table valueCellFlexGrow={3}>
        {WorkbookRow(props)}
        {ProjectRow(props)}
        {AuthorRow(props)}
        {props.modifiedDate ? DateRow(props.modifiedDate) : null}
        <LabelWrapper itemInfo={view} category={'warning'} isEager={false} kind={'chip'} popoverPlacement={'left'} />
      </MetadataTable.Table>
    </HeaderWrapper>
  );
}


function WorkbookRow(props: HeaderDetailsProps): JSX.Element {
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <MetadataTable.Row>
          <MetadataTable.LabelCell data-tb-test-id='data-details-workbook-header-label'>
            {Messages.WorkbookHeaderLabel()}
          </MetadataTable.LabelCell>
          <MetadataTable.ValueCell data-tb-test-id='data-details-workbook-header-value'>
            {props.workbookName != null ?
              <TextLinkWidget
                href={urls.workbookDetailsUrl(props.workbookVizportalUrlId)}
                target='_blank'
                testId='data-details-workbook-header-link'
              >
                {props.workbookName}
              </TextLinkWidget>
              : ViewerPaneObfuscatedPlaceholder()
            }
          </MetadataTable.ValueCell>
        </MetadataTable.Row>
      }
    </SiteUrlsContext.Consumer>
  );
}

function ProjectRow(props: HeaderDetailsProps): JSX.Element {
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <MetadataTable.Row>
          <MetadataTable.LabelCell data-tb-test-id='data-details-project-header-label'>
            {Messages.DataDetailsProjectLabel()}
          </MetadataTable.LabelCell>
          <MetadataTable.ValueCell data-tb-test-id='data-details-project-header-value'>
            <TextLinkWidget
              href={props.projectVizportalUrlId ? urls.projectUrl(props.projectVizportalUrlId) : undefined}
              target='_blank'
              testId='data-details-project-header-link'
            >
              {props.projectName}
            </TextLinkWidget>
          </MetadataTable.ValueCell>
        </MetadataTable.Row>
      }
    </SiteUrlsContext.Consumer>
  );
}

function AuthorRow(props: HeaderDetailsProps): JSX.Element {
  return (
    <MetadataTable.Row>
      <MetadataTable.LabelCell data-tb-test-id='data-details-author-header-label'>
        {Messages.AuthorHeaderLabel()}
      </MetadataTable.LabelCell>
      <MetadataTable.ValueCell data-tb-test-id='data-details-author-header-value'>
        <UserLinkDisplayComponent
          user={props.owner}
          linkComponent={
            ({ name, url }) => (
              <TextLinkWidget
                href={url}
                target='_blank'
                testId='data-details-author-header-link'
              >
                {name}
              </TextLinkWidget>
            )}
        />
      </MetadataTable.ValueCell>
    </MetadataTable.Row>
  );
}

function DateRow(date: string): JSX.Element {
  // This function assumes that `date` is in ISO 8601 format.
  return (
    <MetadataTable.Row>
      <MetadataTable.LabelCell data-tb-test-id='data-details-date-header-label'>
        {Messages.DateHeaderLabel()}
      </MetadataTable.LabelCell>
      <MetadataTable.ValueCell data-tb-test-id='data-details-date-header-value'>
        {Formatters.formatMediumDateTimeWithoutSeconds(new Date(date))}
      </MetadataTable.ValueCell>
    </MetadataTable.Row>
  );
}
